import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UssdApplicationsService } from '../../../../../services/ussd/ussd-applications.service';
import { UserProfileService } from '../../../../../services/user/user-profile.service';
import { ToastService } from '../../../../../services/toast/toast.service';
import {
  UssdApplication,
  UssdMsisdnImpersonation,
  UssdPromotionRecord,
  UssdRoutingConfiguration,
  UssdUsageRecord,
} from '../../../../../types/ussd.models';
import { PromptCardComponent } from '../../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import { ModalComponent } from '../../../../../components/ui/modal-component/modal-component.component';
import { StatusComponent } from '../../../../../components/ui/status/status.component';
import { RoutingTabComponent } from '../../../../../components/ui/tabs/routing-tab/routing-tab.component';
import { EmptyStateComponent } from '../../../../../components/ui/empty-state/empty-state.component';
import { formatCount, formatWhen } from '../../../../../../utils/sms';
import { csvFileName, downloadCsv, toCsv } from '../../../../../../utils/csv';
import {
  appStatusView,
  authLabel,
  countryName,
  describeHttpError,
  environmentIndex,
  environmentLabel,
  formatDuration,
  maskMsisdn,
  nextEnvironment,
  normaliseAuthType,
  normaliseEnvironment,
  outcomeGroup,
  outcomeStatusView,
  plural,
  promotionStatusView,
  StatusView,
  USSD_ENVIRONMENTS,
  UssdEnvironment,
} from '../../ussd-display';

type DetailTab = 'configuration' | 'routing' | 'sessions' | 'promotions';

interface SessionRow {
  key: string;
  started: string;
  msisdn: string;
  mno: string;
  duration: string;
  hops: string;
  outcome: StatusView;
}

interface SessionBreakdown {
  total: number;
  completed: number;
  user: number;
  failed: number;
  open: number;
  /** Failure reasons, largest first. */
  failures: { label: string; count: number }[];
  averageDuration: string;
  averageHops: string;
}

/** Sessions drawn as rows; the CSV export carries all of them. */
const MAX_SESSION_ROWS = 200;

@Component({
  selector: 'app-ussd-application-detail',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    MatTooltipModule,
    PromptCardComponent,
    ModalComponent,
    StatusComponent,
    RoutingTabComponent,
    EmptyStateComponent,
  ],
  templateUrl: './application-detail.component.html',
  styleUrl: './application-detail.component.css',
})
export class UssdApplicationDetailComponent implements OnInit {
  @Input() id!: string;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private applicationsService = inject(UssdApplicationsService);
  private userService = inject(UserProfileService);
  private toast = inject(ToastService);

  app: UssdApplication | null = null;
  isLoading = false;
  loadErrorMsg = '';

  activeTab: DetailTab = 'configuration';

  isTogglingStatus = false;
  showDisableConfirm = false;

  isDeleting = false;
  showDeleteConfirm = false;

  showPromoteModal = false;
  isPromoting = false;
  promotionNotes = '';
  serviceCode = '';
  serviceCodeTouched = false;
  promoteError = '';

  promotionHistory: UssdPromotionRecord[] = [];
  isLoadingPromotions = false;
  promotionsLoaded = false;
  promotionHistoryErrorMsg = '';

  sessionWindowDays = 7;
  sessions: UssdUsageRecord[] = [];
  sessionRows: SessionRow[] = [];
  breakdown: SessionBreakdown | null = null;
  isLoadingSessions = false;
  sessionsLoaded = false;
  sessionsErrorMsg = '';

  readonly ENV_STAGES = USSD_ENVIRONMENTS;
  readonly sessionWindows = [
    { days: 1, label: 'Last 24 hours' },
    { days: 7, label: 'Last 7 days' },
    { days: 30, label: 'Last 30 days' },
  ];
  readonly environmentLabel = environmentLabel;
  readonly formatWhen = formatWhen;
  readonly formatCount = formatCount;
  readonly promotionStatusView = promotionStatusView;
  readonly plural = plural;

  private readonly TABS: DetailTab[] = ['configuration', 'routing', 'sessions', 'promotions'];

  // ── Derived view ──

  get organisationId(): string {
    return this.userService.organizationId ?? '';
  }

  get environment(): UssdEnvironment {
    return normaliseEnvironment(this.app?.environment);
  }

  get status(): StatusView {
    return appStatusView(this.app);
  }

  get isEnabled(): boolean {
    return this.app?.enabled !== false;
  }

  get country(): string {
    return this.app ? countryName(this.app.countryCode, this.app.countryName) : '—';
  }

  get routing(): UssdRoutingConfiguration[] {
    return this.app?.routingConfigurations ?? [];
  }

  get impersonations(): UssdMsisdnImpersonation[] {
    return this.app?.ussdApplicationMsisdnImpersonations ?? [];
  }

  get tabs(): { title: string; link: string; queryParams: Record<string, string>; count?: number }[] {
    const link = `/organization/ussd-simulator/applications/${this.id}`;
    return [
      { title: 'Configuration', link, queryParams: {} },
      { title: 'Routing', link, queryParams: { tab: 'routing' }, count: this.routing.length },
      { title: 'Sessions', link, queryParams: { tab: 'sessions' } },
      { title: 'Promotions', link, queryParams: { tab: 'promotions' } },
    ];
  }

  get authType(): string {
    return normaliseAuthType(this.app?.callbackAuthType).type;
  }

  get authTypeLabel(): string {
    return authLabel(this.app?.callbackAuthType);
  }

  get authIsLegacy(): boolean {
    return !!normaliseAuthType(this.app?.callbackAuthType).legacy;
  }

  /** The newest promotion still waiting for a reviewer, if any. */
  get pendingPromotion(): UssdPromotionRecord | null {
    return (
      this.sortedPromotions.find((p) => (p.status || '').toUpperCase() === 'PENDING_APPROVAL') ?? null
    );
  }

  get sortedPromotions(): UssdPromotionRecord[] {
    const when = (p: UssdPromotionRecord) =>
      new Date(p.requestedAt || p.promotedAt || p.reviewedAt || 0).getTime() || 0;
    return [...this.promotionHistory].sort((a, b) => when(b) - when(a));
  }

  get nextStage(): UssdEnvironment | null {
    return nextEnvironment(this.environment);
  }

  get canPromote(): boolean {
    return !!this.nextStage && !this.pendingPromotion;
  }

  get isPromotingToProduction(): boolean {
    return this.nextStage === 'PRODUCTION';
  }

  get promoteButtonTitle(): string {
    if (!this.nextStage) return 'Promote';
    return this.isPromotingToProduction
      ? 'Request production approval'
      : `Promote to ${environmentLabel(this.nextStage)}`;
  }

  get isPromoteDisabled(): boolean {
    if (!this.nextStage || this.isPromoting) return true;
    return this.isPromotingToProduction && !this.serviceCode.trim();
  }

  stageState(index: number): 'done' | 'current' | 'pending' | 'todo' {
    const current = environmentIndex(this.environment);
    if (index < current) return 'done';
    if (index === current) return 'current';
    const pending = this.pendingPromotion;
    if (pending && normaliseEnvironment(pending.targetEnvironment) === USSD_ENVIRONMENTS[index]?.key) {
      return 'pending';
    }
    return 'todo';
  }

  secret(value: string | null | undefined): string {
    return value ? '••••••••••••' : 'Not set';
  }

  // ── Lifecycle ──

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const tab = params.get('tab') as DetailTab | null;
      this.setTab(tab && this.TABS.includes(tab) ? tab : 'configuration');
    });
    this.loadApplication();
  }

  loadApplication(): void {
    const organisationId = this.organisationId;
    if (!organisationId) return;

    this.isLoading = !this.app;
    this.loadErrorMsg = '';
    this.applicationsService
      .getApplicationById(organisationId, this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.app = data;
          this.isLoading = false;
          if (!this.promotionsLoaded) this.loadPromotionHistory();
          if (this.activeTab === 'sessions' && !this.sessionsLoaded) this.loadSessions();
        },
        error: (err) => {
          this.app = null;
          this.isLoading = false;
          this.loadErrorMsg =
            err?.status === 404
              ? ''
              : describeHttpError(err, 'This application could not be loaded.');
        },
      });
  }

  setTab(tab: DetailTab): void {
    this.activeTab = tab;
    if (tab === 'promotions' && !this.promotionsLoaded && this.app) this.loadPromotionHistory();
    if (tab === 'sessions' && !this.sessionsLoaded && this.app) this.loadSessions();
  }

  // ── Promotion history ──

  loadPromotionHistory(): void {
    if (!this.app || !this.organisationId) return;

    this.isLoadingPromotions = true;
    this.promotionHistoryErrorMsg = '';
    this.applicationsService.getPromotionHistory(this.organisationId, this.app.id).subscribe({
      next: (records) => {
        this.promotionHistory = Array.isArray(records) ? records : [];
        this.promotionsLoaded = true;
        this.isLoadingPromotions = false;
      },
      error: (err) => {
        this.promotionsLoaded = true;
        this.isLoadingPromotions = false;
        this.promotionHistoryErrorMsg = describeHttpError(err, 'Promotion history could not be loaded.');
      },
    });
  }

  // ── Sessions ──

  setSessionWindow(days: number): void {
    if (days === this.sessionWindowDays) return;
    this.sessionWindowDays = days;
    this.loadSessions();
  }

  loadSessions(): void {
    if (!this.app || !this.organisationId) return;

    const to = new Date();
    const from = new Date(to.getTime() - this.sessionWindowDays * 24 * 60 * 60 * 1000);
    const app = this.app;

    this.isLoadingSessions = true;
    this.sessionsErrorMsg = '';
    this.applicationsService.getUsageRecords(this.organisationId, from, to).subscribe({
      next: (records) => {
        const ids = new Set([app.id, app.uuid].filter(Boolean).map(String));
        this.sessions = records
          .filter((r) =>
            r.applicationId ? ids.has(String(r.applicationId)) : !!r.serviceCode && r.serviceCode === app.serviceCode
          )
          .sort((a, b) => new Date(b.sessionStart || 0).getTime() - new Date(a.sessionStart || 0).getTime());
        this.buildSessions();
        this.sessionsLoaded = true;
        this.isLoadingSessions = false;
      },
      error: (err) => {
        this.sessions = [];
        this.buildSessions();
        this.sessionsLoaded = true;
        this.isLoadingSessions = false;
        this.sessionsErrorMsg = describeHttpError(err, 'Sessions could not be loaded.');
      },
    });
  }

  private buildSessions(): void {
    const records = this.sessions;
    this.sessionRows = records.slice(0, MAX_SESSION_ROWS).map((r, i) => ({
      key: `${r.sessionId || 'session'}-${i}`,
      started: r.sessionStart ? formatWhen(r.sessionStart) : '—',
      msisdn: maskMsisdn(r.msisdn),
      mno: r.mno || '—',
      duration: formatDuration(r.durationMs),
      hops: r.hops === null || r.hops === undefined ? '—' : String(r.hops),
      outcome: outcomeStatusView(r.outcome),
    }));

    if (!records.length) {
      this.breakdown = null;
      return;
    }

    const counts = { completed: 0, user: 0, failed: 0, open: 0 };
    const failures = new Map<string, number>();
    let durationSum = 0;
    let durationCount = 0;
    let hopSum = 0;
    let hopCount = 0;
    for (const r of records) {
      const group = outcomeGroup(r.outcome);
      counts[group] += 1;
      if (group === 'failed') {
        const label = outcomeStatusView(r.outcome).text;
        failures.set(label, (failures.get(label) ?? 0) + 1);
      }
      if (typeof r.durationMs === 'number' && Number.isFinite(r.durationMs)) {
        durationSum += r.durationMs;
        durationCount += 1;
      }
      if (typeof r.hops === 'number' && Number.isFinite(r.hops)) {
        hopSum += r.hops;
        hopCount += 1;
      }
    }

    this.breakdown = {
      total: records.length,
      ...counts,
      failures: [...failures.entries()]
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count),
      averageDuration: durationCount ? formatDuration(durationSum / durationCount) : '—',
      averageHops: hopCount ? (hopSum / hopCount).toFixed(1).replace(/\.0$/, '') : '—',
    };
  }

  share(count: number): string {
    const total = this.breakdown?.total ?? 0;
    if (!total) return '0%';
    const pct = (count / total) * 100;
    return `${pct > 0 && pct < 1 ? '<1' : Math.round(pct)}%`;
  }

  downloadSessions(): void {
    if (!this.app || !this.sessions.length) return;
    const csv = toCsv(this.sessions, [
      { label: 'Session ID', value: (r) => r.sessionId },
      { label: 'Started', value: (r) => r.sessionStart ?? '' },
      { label: 'Ended', value: (r) => r.sessionEnd ?? '' },
      { label: 'MSISDN', value: (r) => maskMsisdn(r.msisdn) },
      { label: 'Network', value: (r) => r.mno ?? '' },
      { label: 'Duration (ms)', value: (r) => r.durationMs ?? '' },
      { label: 'Steps', value: (r) => r.hops ?? '' },
      { label: 'Outcome', value: (r) => outcomeStatusView(r.outcome).text },
    ]);
    downloadCsv(csvFileName(this.app.name, 'ussd-sessions'), csv);
  }

  // ── Enable / disable ──

  toggleStatus(): void {
    if (!this.app || this.isTogglingStatus) return;
    if (this.isEnabled) {
      this.showDisableConfirm = true;
      return;
    }
    this.executeToggle();
  }

  confirmDisable(): void {
    this.showDisableConfirm = false;
    this.executeToggle();
  }

  cancelDisable(): void {
    this.showDisableConfirm = false;
  }

  private executeToggle(): void {
    if (!this.app || !this.organisationId) return;

    const enabling = !this.isEnabled;
    const call = enabling
      ? this.applicationsService.enableApplication(this.organisationId, this.app.id)
      : this.applicationsService.disableApplication(this.organisationId, this.app.id);

    this.isTogglingStatus = true;
    call.subscribe({
      next: (updated) => {
        const enabled = updated?.enabled ?? enabling;
        if (this.app) this.app = { ...this.app, ...(updated ?? {}), enabled };
        this.toast.success(enabled ? `${this.app?.name} enabled.` : `${this.app?.name} disabled. Its code no longer answers.`);
        this.isTogglingStatus = false;
      },
      error: (err) => {
        this.toast.error(
          describeHttpError(err, enabling ? 'The application was not enabled.' : 'The application was not disabled.')
        );
        this.isTogglingStatus = false;
      },
    });
  }

  // ── Delete ──

  confirmDelete(): void {
    this.showDeleteConfirm = true;
  }

  cancelDelete(): void {
    this.showDeleteConfirm = false;
  }

  deleteApplication(): void {
    if (!this.app || this.isDeleting || !this.organisationId) return;

    const name = this.app.name;
    this.isDeleting = true;
    this.applicationsService.deleteApplication(this.organisationId, this.app.id).subscribe({
      next: () => {
        this.toast.success(`${name} deleted.`);
        this.showDeleteConfirm = false;
        this.isDeleting = false;
        this.router.navigate(['/organization/ussd-simulator/applications']);
      },
      error: (err) => {
        this.toast.error(describeHttpError(err, `${name} was not deleted.`));
        this.showDeleteConfirm = false;
        this.isDeleting = false;
      },
    });
  }

  // ── Promote (one stage forward; production waits for approval) ──

  openPromoteModal(): void {
    if (!this.canPromote) return;
    this.showPromoteModal = true;
    this.promotionNotes = '';
    this.serviceCode = '';
    this.serviceCodeTouched = false;
    this.promoteError = '';
  }

  cancelPromote(): void {
    if (this.isPromoting) return;
    this.showPromoteModal = false;
    this.promotionNotes = '';
    this.serviceCode = '';
    this.serviceCodeTouched = false;
    this.promoteError = '';
  }

  confirmPromote(): void {
    const target = this.nextStage;
    if (!this.app || !target || this.isPromoting) return;
    if (this.isPromotingToProduction && !this.serviceCode.trim()) {
      this.serviceCodeTouched = true;
      return;
    }
    if (!this.organisationId) return;

    const requestedBy: string =
      this.userService.userDetails?.preferred_username ||
      this.userService.userDetails?.email ||
      this.userService.userDetails?.name ||
      'unknown';

    const source = this.environment;
    this.isPromoting = true;
    this.promoteError = '';
    this.applicationsService
      .promoteApplication(this.organisationId, this.app.id, {
        sourceEnvironment: source,
        targetEnvironment: target,
        requestedBy,
        promotionNotes: this.promotionNotes.trim() || undefined,
        serviceCode: this.isPromotingToProduction ? this.serviceCode.trim() : undefined,
      })
      .subscribe({
        next: (record) => {
          const status = (record?.status || '').toUpperCase();
          const label = environmentLabel(target);
          if (status === 'PENDING_APPROVAL' || (target === 'PRODUCTION' && !status)) {
            this.toast.success(
              `Approval requested for ${label}. The app stays in ${environmentLabel(source)} until a reviewer approves it.`
            );
          } else if (status === 'REJECTED' || status === 'CANCELLED') {
            this.toast.error(`The promotion to ${label} was ${status.toLowerCase()}.`);
          } else {
            this.toast.success(`Promoted to ${label}.`);
          }
          this.isPromoting = false;
          this.showPromoteModal = false;
          this.promotionNotes = '';
          this.serviceCode = '';
          this.serviceCodeTouched = false;
          // The server decides what changed; show its version.
          this.loadApplication();
          this.loadPromotionHistory();
        },
        error: (err) => {
          this.isPromoting = false;
          this.promoteError = describeHttpError(err, 'The promotion was not requested.');
        },
      });
  }

  // ── Navigation ──

  goBack(): void {
    this.router.navigate(['/organization/ussd-simulator/applications']);
  }

  goToEdit(): void {
    this.router.navigate(['/organization/ussd-simulator/applications', this.id, 'edit']);
  }

  testInSimulator(): void {
    if (!this.app) return;
    this.router.navigate(['/organization/ussd-simulator/simulator'], {
      queryParams: { code: this.app.serviceCode || null, country: this.app.countryCode || null },
    });
  }
}
